import {
  Box,
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  MultiSelect,
  Loader,
} from '@mantine/core';
import { IconMessage2 } from '@tabler/icons-react';
import { useMemo } from 'react';

import { DEPARTMENTS_STRUCTURE, SUB_DEPARTMENTS } from '../../constants';
import { useBotPageForm } from '../../hooks/useBotPageForm';
import { botTypes } from '../../types';

import { InstPage, TgPage, useGetPages } from '@/features/pages';
import { Platforms } from '@/types';

type SubmitValues = Omit<botTypes.Page, '_id'>;

type PageFormProps = {
  onSubmit: (data: SubmitValues) => void;
  isLoading: boolean;
  initialValues: botTypes.PageFormValues;
  isEdit?: boolean;
};

export const PageForm = ({ onSubmit, isLoading, initialValues, isEdit }: PageFormProps) => {
  const form = useBotPageForm({ initialValues });

  const getTgPages = useGetPages({
    platform: Platforms.telegram,
    department: initialValues.department,
  });

  const getInstPages = useGetPages({
    platform: Platforms.instagram,
    department: initialValues.department,
  });

  const isShowingLoader = isLoading || getTgPages.isLoading || getInstPages.isLoading;

  const options = useMemo(() => {
    const formatPages = (pages: TgPage[] | InstPage[]) =>
      pages.map((page) => ({
        label: page.name + (page.username ? ` (${page.username})` : ''),
        value: page._id,
      }));

    return [
      { group: 'Telegram', items: formatPages(getTgPages.data?.pages || []) },
      { group: 'Instagram', items: formatPages(getInstPages.data?.pages || []) },
    ];
  }, [getTgPages.data, getInstPages.data]);

  const isFormError = Object.keys(form.errors).length > 0;

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        if (values.subDepartment) {
          onSubmit({ ...values, subDepartment: values.subDepartment });
        }
      })}
    >
      <TextInput
        label="Name"
        required
        withAsterisk
        autoComplete="off"
        placeholder="Name"
        value={form.values.name}
        onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
        my="sm"
        error={form.errors.name}
      />

      <TextInput
        label="Id"
        required
        withAsterisk
        autoComplete="off"
        placeholder="Id"
        value={form.values.externalId}
        onChange={(event) => form.setFieldValue('externalId', event.currentTarget.value)}
        my="sm"
        error={form.errors.externalId}
      />

      <Select
        label="Sub-department"
        withAsterisk
        data={DEPARTMENTS_STRUCTURE[initialValues.department].map(
          (option: botTypes.SubDepartments) => ({ value: option, label: SUB_DEPARTMENTS[option] })
        )}
        value={form.values.subDepartment}
        onChange={(value) => {
          if (
            value &&
            Object.values(botTypes.SubDepartments).includes(value as botTypes.SubDepartments)
          ) {
            form.setFieldValue('subDepartment', value as botTypes.SubDepartments);
          }
        }}
        error={form.errors.subDepartment}
        mb="sm"
      />

      <MultiSelect
        label="Linked pages"
        value={form.values.appPages}
        data={options}
        placeholder="Select pages"
        searchable
        hidePickedOptions
        rightSection={isShowingLoader ? <Loader size="xs" /> : null}
        leftSection={<IconMessage2 size="1rem" />}
        onChange={(value) => form.setFieldValue('appPages', value)}
        error={form.errors.appPages}
      />

      <Stack gap="xs" my="sm">
        <Checkbox
          label="Expert"
          checked={!!form.values.isExpert}
          onChange={(e) => form.setFieldValue('isExpert', e.currentTarget.checked)}
        />
        <Checkbox
          label="Mandatory manager select"
          checked={!!form.values.isMandatoryManagerSelect}
          onChange={(e) => form.setFieldValue('isMandatoryManagerSelect', e.currentTarget.checked)}
        />
      </Stack>

      <TextInput
        label="Department"
        required
        readOnly
        {...form.getInputProps('department')}
        disabled
        mb="xl"
      />
      <Box h={25}>
        {isFormError && (
          <Text c="red" size="sm" ta="end">
            {Object.values(form.errors)[0] || 'Error'}
          </Text>
        )}
      </Box>

      <Group justify="flex-end">
        <Button loading={isLoading} type="submit">
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </Group>
    </form>
  );
};
